import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Copy,
  Check,
  Lock,
  Camera,
  Pencil,
  Eye,
  FileText,
  Zap,
  ChevronRight
} from 'lucide-react';

import ImageCropperModal from '../../molecules/ImageCropperModal/view';
import PasswordChangeModal from '../../molecules/PasswordChangeModal/view';
import ElectronicDocumentConsumption from '../../organisms/ElectronicDocumentConsumption/view';
import ButtonPrimary from '../../atoms/ButtonPrimary/view';
import ButtonSecondary from '../../atoms/ButtonSecondary/view';
import { dispatchCustomEvent } from '../../../utils/dispatch';
import { updateUser, uploadAvatar, updatePassword } from '../../../usecases/users-usecases';
import { getMarketplacePackages, purchaseMarketplacePackage } from '../../../usecases/marketplace-usecase';
import './style.css';
import { Col, Row } from 'react-bootstrap';

function ProfileForm({ profile, plan, showSubscription = true }) {
  const navigate = useNavigate();

  const [profileImage, setProfileImage] = useState(profile?.url_avatar || '');
  const [isDisableSave, setDisableSave] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [packages, setPackages] = useState([]);
  const [buyingId, setBuyingId] = useState(null);

  // Image cropping states
  const [showCropper, setShowCropper] = useState(false);
  const [tempImage, setTempImage] = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    companyName: profile?.companyName || '',
    name: profile?.name || '',
    phone: profile?.phone || '',
    address: profile?.address || '',
    url_avatar: profile?.url_avatar || '',
  });

  useEffect(() => {
    if (profile) {
      setProfileImage(profile.url_avatar);
      setFormData({
        companyName: profile.companyName || '',
        name: profile.name || '',
        phone: profile.phone || '',
        address: profile.address || '',
        url_avatar: profile.url_avatar || '',
      });
    }
  }, [profile]);

  useEffect(() => {
    getMarketplacePackages()
      .then((res) => {
        const list = res?.data?.data || res?.data || [];
        setPackages(list);
      })
      .catch(() => setPackages([]));
  }, []);

  const handleBuyPackage = async (pkg) => {
    if (buyingId) return;
    setBuyingId(pkg.id);
    try {
      await purchaseMarketplacePackage(Number(pkg.id));
      dispatchCustomEvent('popup', { message: `¡${pkg.name} adquirido con éxito!`, type: 'success' });
      dispatchCustomEvent('update-subscription', null);
    } catch {
      dispatchCustomEvent('popup', { message: 'Error al adquirir el paquete. Verifica tu método de pago.', type: 'error' });
    } finally {
      setBuyingId(null);
    }
  };

  const handleChange = async (e) => {
    const { name, value, type } = e.target;
    let processedValue = type === "number" ? parseInt(value, 10) : value;

    if (type === "file") {
      if (e.target.files && e.target.files.length > 0) {
        const file = e.target.files[0];
        const acceptedFormats = ['image/jpeg', 'image/png', 'image/webp'];
        if (!acceptedFormats.includes(file.type)) {
          dispatchCustomEvent("popup", { message: "❌ Formato no soportado. Por favor cargue imágenes JPEG o PNG.", type: "error" });
          return;
        }
        const maxSizeInBytes = 5 * 1024 * 1024;
        if (file.size > maxSizeInBytes) {
          dispatchCustomEvent("popup", { message: "❌ La imagen es demasiado grande. Máximo 5MB.", type: "error" });
          return;
        }

        const reader = new FileReader();
        reader.onload = () => {
          setTempImage(reader.result);
          setShowCropper(true);
        };
        reader.readAsDataURL(file);
      }
      e.target.value = '';
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));
  };

  const handleCropSave = async (croppedImageBlob) => {
    setShowCropper(false);
    setDisableSave(true);

    if (!croppedImageBlob) {
      dispatchCustomEvent("popup", { message: "❌ No se seleccionó ninguna imagen", type: "error" });
      setDisableSave(false);
      return;
    }

    try {
      const file = new File([croppedImageBlob], "avatar.jpg", { type: "image/jpeg" });
      const url = await uploadAvatar(profile.uid, profile.cid, file);
      if (url) {
        setProfileImage(url);
        setFormData((prev) => ({
          ...prev,
          url_avatar: url,
        }));
        dispatchCustomEvent("popup", { message: "✅ Imagen actualizada correctamente", type: "success" });
      }
    } catch (error) {
      dispatchCustomEvent("popup", { message: "❌ Error al cargar la imagen", type: "error" });
    }

    setDisableSave(false);
    setTempImage(null);
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setTempImage(null);
  };

  const handleImageClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCopyUid = () => {
    navigator.clipboard.writeText(profile?.uid || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setDisableSave(true);
    const response = await updateUser(profile.uid, profile.cid, formData);
    if (response) {
      dispatchCustomEvent("update-profile", response);
      dispatchCustomEvent("popup", { message: "✅ Perfil actualizado" });
      setIsEditing(false);
    } else {
      dispatchCustomEvent("popup", { message: "❌ Error al actualizar el perfil" });
    }
    setDisableSave(false);
  }

  const handleOpenPasswordModal = () => {
    dispatchCustomEvent('modalprofile-password-change');
  };

  const handlePasswordUpdateSubmit = async (newPassword) => {
    await updatePassword(profile.uid, profile.cid, newPassword);
    dispatchCustomEvent('popup', { message: '✅ Contraseña actualizada exitosamente', type: 'success' });
    dispatchCustomEvent('close-profile-password-change-modal');
  };

  const planName = plan && plan !== 'no_suscription' ? (plan.name || 'Suscripción Activa') : 'Sin Suscripción';

  return (
    <div className="profile-container-premium">
      {/* CARD 1: Profile Details */}
      <div className="profile-card-premium animate-in">
        {/* Header Section */}
        <div className="profile-header-row">
          <div className="profile-header-left">
            <div
              className={`profile-avatar-wrapper-premium ${isEditing ? 'editable' : ''}`}
              onClick={isEditing ? handleImageClick : undefined}
              title={isEditing ? "Cambiar foto de perfil" : undefined}
            >
              <img
                src={profileImage || 'https://via.placeholder.com/150'}
                alt="Avatar"
                className="profile-avatar-premium"
              />
              {isEditing && (
                <div className="profile-avatar-overlay-premium">
                  <Camera size={16} />
                </div>
              )}
            </div>
            <div className="profile-header-info">
              <span className="profile-tag-detail">Cuenta Personal</span>
              <div className="profile-plan-value">{formData.name || 'Cargando...'}</div>
              <div className="profile-plan-status">
                {formData.companyName || 'Sin Empresa'}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="profile-edit-toggle-btn"
            onClick={() => setIsEditing(!isEditing)}
            title={isEditing ? "Ver información" : "Editar perfil"}
          >
            {isEditing ? <Eye size={16} /> : <Pencil size={16} />}
          </button>
        </div>

        {/* Input Form Fields Grid */}
        <form onSubmit={handleSubmit} className="profile-form-grid">
          <div className="profile-form-field">
            <label className="profile-field-label">Nombre de la Empresa</label>
            {isEditing ? (
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Nombre de tu negocio"
                className="profile-field-input"
              />
            ) : (
              <div className="profile-field-readonly-wrapper">
                <span className="profile-field-readonly-value">{formData.companyName || 'N/A'}</span>
              </div>
            )}
          </div>

          <div className="profile-form-field">
            <label className="profile-field-label">Nombre del Propietario</label>
            {isEditing ? (
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Tu nombre completo"
                className="profile-field-input"
              />
            ) : (
              <div className="profile-field-readonly-wrapper">
                <span className="profile-field-readonly-value">{formData.name || 'N/A'}</span>
              </div>
            )}
          </div>

          <div className="profile-form-field">
            <label className="profile-field-label">Teléfono</label>
            {isEditing ? (
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Número de contacto"
                className="profile-field-input"
              />
            ) : (
              <div className="profile-field-readonly-wrapper">
                <span className="profile-field-readonly-value">{formData.phone || 'N/A'}</span>
              </div>
            )}
          </div>

          <div className="profile-form-field">
            <label className="profile-field-label">Dirección</label>
            {isEditing ? (
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Dirección física"
                className="profile-field-input"
              />
            ) : (
              <div className="profile-field-readonly-wrapper">
                <span className="profile-field-readonly-value">{formData.address || 'N/A'}</span>
              </div>
            )}
          </div>

          {/* System Footer Info */}
          <div className="profile-form-field span-full">
            <label className="profile-field-label">Correo Electrónico</label>
            <div className="profile-field-readonly-value font-medium">{profile?.email || 'N/A'}</div>
          </div>

        </form>

        {isEditing && (
          <ButtonPrimary
            id="submit-profile-btn"
            title={isDisableSave ? 'Guardando...' : 'Guardar Cambios'}
            disabled={isDisableSave}
            onClick={handleSubmit}
            style={{ width: '100%', padding: '14px', height: 'auto' }}
          />
        )}

        <ButtonSecondary
          id="open-password-modal"
          title="Cambiar Contraseña"
          icon={<Lock size={14} />}
          onClick={handleOpenPasswordModal}
          style={{ width: '100%', padding: '12px', height: 'auto' }}
        />
      </div>

      {/* CARD 2: Plan Info and Monthly Consumption */}
      {showSubscription && profile && (
        <div className="profile-card-premium animate-in">
          {/* Plan Header */}
          <div className="profile-header-row">
            <div className="profile-header-left">
              <div className="profile-plan-icon-box">
                <Calendar size={22} />
              </div>
              <div className="profile-header-info">
                <div className="profile-plan-label-wrapper">
                  <span className="profile-plan-label">Plan de Suscripción</span>
                </div>
                <div className="profile-plan-value">{planName}</div>
                <div hidden className="profile-plan-status" onClick={() => navigate('/planes')}>
                  Ver todos los planes &rarr;
                </div>
              </div>
            </div>
          </div>

          <div className="profile-form-divider" />

          {/* Plan Body / Consumptions */}
          <div className="profile-plan-body">
            {plan === 'no_suscription' || !plan ? (
              <div className="profile-no-plan-wrapper">
                <p className="profile-no-plan-text">No tienes una suscripción activa actualmente.</p>
                <button
                  type="button"
                  className="profile-submit-btn"
                  onClick={() => navigate("/planes")}
                >
                  Ver Planes Disponibles
                </button>
              </div>
            ) : (
              <div className="profile-consumption-wrapper">
                <ElectronicDocumentConsumption />
              </div>
            )}
          </div>
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleChange}
        accept="image/*"
      />

      <ImageCropperModal
        show={showCropper}
        imageSrc={tempImage}
        onCancel={handleCropCancel}
        onSave={handleCropSave}
      />

      <PasswordChangeModal
        id="profile-password-change"
        userEmail={profile?.email}
        onUpdatePassword={handlePasswordUpdateSubmit}
      />
    </div>
  );
}

export default ProfileForm;
